import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Spin, Result, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageContainer } from './styles/SubscriptionsPage.styles';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';

const { Title, Text } = Typography;

interface ExtendedLicenseInfo {
    hasExtendedLicense: boolean;
    extendedLicensePrice: number;
    canCreateTournament: boolean;
}

const ExtendedLicensePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const fromCreate = (location.state as any)?.fromCreate === true;
    const priceFromState = (location.state as any)?.extendedLicensePrice as number | undefined;

    const [info, setInfo] = useState<ExtendedLicenseInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);

    useEffect(() => {
        const fetchInfo = async () => {
            try {
                setLoading(true);
                const res = await apiService.get<ApiResponse<ExtendedLicenseInfo>>(
                    API_ENDPOINTS.PAYMENTS.EXTENDED_LICENSE_INFO
                );
                const data = res.data?.data;
                if (data) setInfo(data);
            } catch (e) {
                logger.error('Error fetching extended license info:', e);
                message.error('Не удалось загрузить данные');
            } finally {
                setLoading(false);
            }
        };
        fetchInfo();
    }, []);

    const handlePay = async () => {
        try {
            setPaying(true);
            const res = await apiService.post<ApiResponse<{ redirectUrl: string; amount: { value: number; currency: string } }>>(
                API_ENDPOINTS.PAYMENTS.EXTENDED_LICENSE
            );
            const redirectUrl = res.data?.data?.redirectUrl;
            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                message.error('Не получена ссылка на оплату');
            }
        } catch (e: any) {
            logger.error('Error creating extended license payment:', e);
            message.error(e.response?.data?.message || 'Ошибка при создании платежа');
        } finally {
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Card>
                    <div style={{ textAlign: 'center', padding: 24 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>Загрузка...</div>
                    </div>
                </Card>
            </PageContainer>
        );
    }

    const price = info?.extendedLicensePrice ?? priceFromState ?? 0;
    const canCreate = info?.canCreateTournament ?? false;
    const hasLicense = info?.hasExtendedLicense ?? false;

    if (canCreate && hasLicense) {
        return (
            <PageContainer>
                <Card>
                    <Result
                        status="success"
                        title="Расширенная лицензия оформлена"
                        subTitle="Ваш клуб может создавать турниры."
                        extra={[
                            <Button type="primary" key="create" onClick={() => navigate('/tournaments/create')}>
                                Создать турнир
                            </Button>,
                            <Button key="list" onClick={() => navigate('/tournaments')}>
                                К списку турниров
                            </Button>
                        ]}
                    />
                </Card>
            </PageContainer>
        );
    }

    if (canCreate && price <= 0) {
        return (
            <PageContainer>
                <Card>
                    <Result
                        status="info"
                        title="Создание турниров доступно"
                        subTitle="Стоимость расширенной лицензии не задана. Вы можете создавать турниры."
                        extra={
                            <Button type="primary" onClick={() => navigate('/tournaments/create')}>
                                Создать турнир
                            </Button>
                        }
                    />
                </Card>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Card>
                <Title level={2}>Расширенная лицензия</Title>
                <p>
                    Для создания турниров клубу необходима расширенная лицензия.
                    {fromCreate && ' Оформите оплату, после чего вы сможете создать турнир.'}
                </p>
                <p>
                    <Text strong>Стоимость: {price} ₽</Text>
                </p>
                <div style={{ marginTop: 24 }}>
                    <Button type="primary" size="large" loading={paying} onClick={handlePay}>
                        Оплатить расширенную лицензию
                    </Button>
                    <Button style={{ marginLeft: 12 }} onClick={() => navigate('/tournaments')}>
                        Отмена
                    </Button>
                </div>
            </Card>
        </PageContainer>
    );
};

export default ExtendedLicensePage;
